import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Clipboard,
  ClipboardList,
  Download,
  FileText,
  FileUp,
  Package,
  Printer,
  Ruler,
  Save,
  Scissors,
  Send,
  Sparkles,
  Trash2,
  User,
  Wrench,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { apiError, fmtARS } from "@/lib/api";

const COLORS = [
  { label: "Negro satinado", value: "#151515" },
  { label: "Gris grafito", value: "#3F4448" },
  { label: "Blanco", value: "#F5F2EA" },
  { label: "Oxido", value: "#8A4B2E" },
  { label: "Verde oscuro", value: "#1F3F32" },
];

const IMPORTED_EXAMPLES = [
  {
    label: "Batientes con foto",
    text: "Hola bro buen dia. Consulta estos 2 portones son abrir batientes y el de la derecha llega sin parante hasta arriba, se puede? Adjunte fotos.",
  },
  {
    label: "Service consorcio",
    text: "Necesitamos que mandes a alguien que se fije el porton de la cochera, esta andando mal. No esta levantado bien, se traba y no abre. Abre hasta la mitad y en manual tampoco funciona. Calle 33 entre 4 y 5.",
  },
  {
    label: "Chapa con buñas",
    text: "Me pasas para hacer estos portones y puerta en chapa 18 con 3 buñas horizontales. Armo con caño. Tener en cuenta los dos recibidores para los portones y el marco para la puerta.",
  },
  {
    label: "Motores y levadizo",
    text: "Necesito 5 motores aereos para portones corredizos. Las hojas son de 2.40 como mucho. Y tambien un motor para un porton levadizo de 2.60 de ancho x 2.40 de alto, peso aproximado 120 kilos.",
  },
];

const FIELD_CLASS = "bg-neutral-950 border-neutral-800 rounded-sm h-10 text-white";
const SELECT_CLASS = "w-full h-10 bg-neutral-950 border border-neutral-800 rounded-sm px-3 text-sm text-white";
const LABEL_CLASS = "text-[11px] uppercase font-bold tracking-[0.12em] text-neutral-400 block mb-1.5";

const FLOW_STEPS = [
  {
    number: "1",
    title: "Consulta",
    detail: "Se carga lo que pidio el cliente o una consulta existente.",
  },
  {
    number: "2",
    title: "Cotizacion",
    detail: "Se arma la vista y el plano para revisar.",
  },
  {
    number: "3",
    title: "Presupuesto",
    detail: "Se cargan materiales, mano de obra y margen.",
  },
  {
    number: "4",
    title: "Orden de trabajo",
    detail: "Cuando se aprueba, pasa a produccion.",
  },
  {
    number: "5",
    title: "Orden de corte",
    detail: "Se genera la lista de cortes para taller.",
  },
];

const fmtMm = (v) => `${Number(v || 0).toLocaleString("es-AR")} mm`;
const mmToMeters = (v) => `${(Number(v || 0) / 1000).toFixed(2)} m`;

const defaultForm = {
  jobNumber: "GR-TR-001",
  client: "",
  phone: "",
  location: "",
  gateType: "corredizo",
  width: 4200,
  height: 2100,
  leaves: 1,
  fill: "chapa plegada",
  frameProfile: "60x40x1.6",
  verticalProfile: "40x40x1.6",
  columnProfile: "100x100x2",
  automation: "si",
  color: "#151515",
  finish: "pintura sintetica",
  notes: "Respetar vano libre, prever guia inferior y espacio lateral de apertura.",
};

const emptyClientDraft = { name: "", phone: "", email: "", address: "", notes: "" };

function parseCustomerRequest(text) {
  const source = text.toLowerCase();
  const next = {};
  const notes = text.trim();
  const decimal = "([0-9]+(?:[\\.,][0-9]+)?)";
  const metersPair = source.match(new RegExp(`${decimal}\\s*(?:m|mts|metros)?\\s*[x×]\\s*${decimal}\\s*(?:m|mts|metros)?`));
  const widthWord = source.match(new RegExp(`(?:ancho|frente|vano)\\D{0,12}${decimal}\\s*(m|mts|metros|mm)?`));
  const heightWord = source.match(new RegExp(`(?:alto|altura)\\D{0,12}${decimal}\\s*(m|mts|metros|mm)?`));

  const toMm = (value, unit = "m") => {
    const parsed = Number(String(value).replace(",", "."));
    if (!Number.isFinite(parsed) || parsed <= 0) return null;
    if (unit === "mm" || parsed > 100) return Math.round(parsed);
    return Math.round(parsed * 1000);
  };

  if (metersPair) {
    const width = toMm(metersPair[1]);
    const height = toMm(metersPair[2]);
    if (width) next.width = width;
    if (height) next.height = height;
  }
  if (widthWord) {
    const width = toMm(widthWord[1], widthWord[2] || "m");
    if (width) next.width = width;
  }
  if (heightWord) {
    const height = toMm(heightWord[1], heightWord[2] || "m");
    if (height) next.height = height;
  }

  if (source.includes("batiente") || source.includes("abrir hacia") || source.includes("dos hojas")) next.gateType = "batiente";
  if (source.includes("corredizo") || source.includes("corredera") || source.includes("riel")) next.gateType = "corredizo";
  if (source.includes("levadizo") || source.includes("contrapeso")) next.gateType = "levadizo";

  const leaves = source.match(/([1-4])\s*(?:hojas|hoja)/);
  if (leaves) next.leaves = Number(leaves[1]);
  else if (source.includes("dos hojas")) next.leaves = 2;

  if (source.includes("barrote")) next.fill = "barrotes";
  else if (source.includes("malla")) next.fill = "malla";
  else if (source.includes("madera")) next.fill = "madera";
  else if (source.includes("chapa lisa")) next.fill = "chapa lisa";
  else if (source.includes("buña") || source.includes("buñas")) next.fill = "chapa plegada";
  else if (source.includes("chapa")) next.fill = "chapa plegada";

  if (source.includes("motor") || source.includes("automat")) next.automation = "si";
  if (source.includes("manual") || source.includes("sin motor")) next.automation = "no";
  if (source.includes("prever")) next.automation = "prever";

  if (source.includes("negro")) next.color = "#151515";
  else if (source.includes("grafito") || source.includes("gris")) next.color = "#3F4448";
  else if (source.includes("blanco")) next.color = "#F5F2EA";
  else if (source.includes("oxido") || source.includes("óxido")) next.color = "#8A4B2E";
  else if (source.includes("verde")) next.color = "#1F3F32";

  if (source.includes("epoxi")) next.finish = "epoxi";
  else if (source.includes("galvanizado")) next.finish = "galvanizado";
  else if (source.includes("sin pintar")) next.finish = "sin pintar";

  if (notes) next.notes = notes;
  return next;
}

function buildCuts(form) {
  const width = Number(form.width);
  const height = Number(form.height);
  const leaves = Math.max(1, Number(form.leaves));
  const leafWidth = Math.round(width / leaves);
  const innerHeight = Math.max(0, height - 120);
  const innerWidth = Math.max(0, leafWidth - 120);
  const verticalBars = form.fill === "barrotes" ? Math.max(3, Math.floor(innerWidth / 140)) : 0;
  const fillPanels = form.fill.includes("chapa") ? leaves : 0;

  const cuts = [
    { label: `Marco superior ${form.frameProfile}`, length: leafWidth, quantity: leaves },
    { label: `Marco inferior ${form.frameProfile}`, length: leafWidth, quantity: leaves },
    { label: `Laterales marco ${form.frameProfile}`, length: height, quantity: leaves * 2 },
    { label: `Refuerzo central ${form.verticalProfile}`, length: innerHeight, quantity: leaves },
  ];

  if (verticalBars) {
    cuts.push({ label: `Barrotes ${form.verticalProfile}`, length: innerHeight, quantity: verticalBars * leaves });
  }

  cuts.push(
    { label: `Columnas ${form.columnProfile}`, length: height + 500, quantity: form.gateType === "batiente" ? 2 : 1 },
    { label: "Guia inferior UPN / angulo", length: width + 700, quantity: form.gateType === "corredizo" ? 1 : 0 },
    { label: "Guias laterales levadizo", length: height + 350, quantity: form.gateType === "levadizo" ? 2 : 0 },
    { label: "Cajon contrapeso", length: height, quantity: form.gateType === "levadizo" ? 2 : 0 },
  );

  if (fillPanels) {
    cuts.push({ label: `Paño ${form.fill}`, length: innerWidth, quantity: fillPanels });
  }

  return cuts.filter((item) => item.quantity > 0);
}

function buildMaterials(form, cuts) {
  const steelMeters = cuts
    .filter((item) => !item.label.startsWith("Pano") && !item.label.startsWith("Paño"))
    .reduce((sum, item) => sum + (item.length * item.quantity) / 1000, 0);

  return [
    { label: "Metros lineales estimados", value: `${steelMeters.toFixed(2)} m` },
    { label: "Sistema", value: form.gateType === "corredizo" ? "guia, rueda y tope" : form.gateType === "levadizo" ? "brazos, guia y contrapesos" : "bisagras reforzadas" },
    { label: "Terminacion", value: form.finish },
    { label: "Automatizacion", value: form.automation === "si" ? "prever motor y rack" : "manual" },
  ];
}

function buildAiPackage(form, cuts, materials) {
  return [
    `Trabajo: ${form.jobNumber}`,
    `Cliente: ${form.client || "sin cliente cargado"}`,
    `Telefono: ${form.phone || "sin telefono"}`,
    `Ubicacion: ${form.location || "sin ubicacion"}`,
    `Tipo: porton ${form.gateType}`,
    `Medidas: ${mmToMeters(form.width)} ancho x ${mmToMeters(form.height)} alto`,
    `Hojas: ${form.leaves}`,
    `Relleno: ${form.fill}`,
    `Perfiles: marco ${form.frameProfile}, interiores ${form.verticalProfile}, columnas ${form.columnProfile}`,
    `Color: ${COLORS.find((c) => c.value === form.color)?.label || form.color}`,
    `Terminacion: ${form.finish}`,
    `Automatizacion: ${form.automation}`,
    "",
    "Detalle de la solicitud:",
    "1. Generar propuesta comercial para enviar al cliente.",
    "2. Sugerir 2 variantes visuales sin cambiar medidas.",
    "3. Marcar dudas tecnicas que falten relevar antes de fabricar.",
    "4. Si hay foto de fachada adjunta, proponer render realista respetando vano, escala y color.",
    "",
    "Restricciones:",
    "- No inventar medidas.",
    "- No modificar ancho, alto ni cantidad de hojas.",
    "- El plano tecnico y lista de cortes los define el sistema.",
    "",
    "Lista de cortes:",
    ...cuts.map((item) => `- ${item.label}: ${item.quantity} x ${fmtMm(item.length)}`),
    "",
    "Materiales:",
    ...materials.map((item) => `- ${item.label}: ${item.value}`),
    "",
    `Notas: ${form.notes || "sin notas"}`,
  ].join("\n");
}

function buildCreatedResponse(form, cuts, materials) {
  const colorName = COLORS.find((c) => c.value === form.color)?.label || "color elegido";
  const system = materials.find((item) => item.label === "Sistema")?.value || "";
  const opening = form.gateType === "corredizo" ? "corredizo" : "batiente";

  return [
    `Propuesta para ${form.client || "cliente"} - ${form.jobNumber}`,
    "",
    `Hola${form.client ? ` ${form.client}` : ""}, te paso una propuesta inicial para el porton ${opening} de ${mmToMeters(form.width)} de ancho por ${mmToMeters(form.height)} de alto.`,
    "",
    `La idea base es fabricarlo con marco ${form.frameProfile}, refuerzos interiores ${form.verticalProfile}, columnas ${form.columnProfile}, relleno en ${form.fill}, terminacion ${form.finish} y color ${colorName}.`,
    `Sistema previsto: ${system}. Automatizacion: ${form.automation}.`,
    "",
    "Antes de cerrar fabricacion conviene confirmar:",
    "- Medida final del vano tomada en obra.",
    "- Nivel del piso y pendiente de apertura.",
    "- Espacio lateral libre para recorrido o apertura.",
    "- Tipo de amure/anclaje de columnas.",
    "- Si hay motor, ubicacion de energia y sentido de apertura.",
    "",
    "Cortes base calculados por el sistema:",
    ...cuts.map((item) => `- ${item.quantity} x ${fmtMm(item.length)} - ${item.label}`),
    "",
    "Notas internas:",
    form.notes || "Sin notas cargadas.",
  ].join("\n");
}

function budgetTotals(items, laborCost, marginPct) {
  const materialsSubtotal = items.reduce((sum, item) => sum + Number(item.quantity || 0) * Number(item.unit_price || 0), 0);
  const subtotal = materialsSubtotal + Number(laborCost || 0);
  const total = subtotal * (1 + Number(marginPct || 0) / 100);
  return { materialsSubtotal, subtotal, total };
}

function buildBudgetText(items, laborCost, marginPct) {
  if (!items.length) return "";
  const totals = budgetTotals(items, laborCost, marginPct);
  return [
    "Presupuesto estimado con precios reales:",
    ...items.map((item) => `- ${item.description}: ${item.quantity} x ${fmtARS(item.unit_price)} = ${fmtARS(Number(item.quantity || 0) * Number(item.unit_price || 0))}`),
    `- Mano de obra: ${fmtARS(laborCost)}`,
    `- Subtotal: ${fmtARS(totals.subtotal)}`,
    `- Margen: ${Number(marginPct || 0)}%`,
    `- Total estimado: ${fmtARS(totals.total)}`,
  ].join("\n");
}

function GatePreview({ form }) {
  const color = form.color;
  const isSliding = form.gateType === "corredizo";
  const isSwing = form.gateType === "batiente";
  const isLift = form.gateType === "levadizo";
  const leafCount = Math.max(1, Number(form.leaves));
  const panelWidth = 620;
  const panelHeight = 310;
  const gateX = 62;
  const gateY = 66;
  const gateW = 492;
  const gateH = 178;
  const leafW = gateW / leafCount;
  const barCount = form.fill === "barrotes" ? 12 : 0;
  const colorName = COLORS.find((c) => c.value === form.color)?.label || "color";

  return (
    <svg viewBox={`0 0 ${panelWidth} ${panelHeight}`} className="w-full h-full" role="img" aria-label="Plano frontal del porton">
      <rect x="0" y="0" width={panelWidth} height={panelHeight} fill="#F3F1EA" />
      <defs>
        <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
          <path d="M20 0H0V20" fill="none" stroke="#DDD8CB" strokeWidth="0.7" />
        </pattern>
        <marker id="arrow" markerWidth="8" markerHeight="8" refX="4" refY="4" orient="auto">
          <path d="M0,0 L8,4 L0,8 z" fill="#151515" />
        </marker>
      </defs>
      <rect x="0" y="0" width={panelWidth} height={panelHeight} fill="url(#grid)" opacity="0.55" />
      <rect x="18" y="16" width="584" height="278" fill="none" stroke="#1E1E1E" strokeWidth="2" />
      <rect x="18" y="16" width="584" height="32" fill="#151515" />
      <text x="32" y="37" fill="#F2B90D" fontSize="14" fontWeight="800" fontFamily="Inter">GR PORTONES · {form.jobNumber}</text>
      <text x="438" y="37" fill="#F2F2F2" fontSize="10" fontFamily="Inter">{form.gateType.toUpperCase()} · {colorName}</text>

      <path d="M34 258H586" stroke="#565656" strokeWidth="3" />
      <rect x={gateX - 22} y={gateY - 22} width="18" height={gateH + 62} fill="#333" stroke="#111" strokeWidth="2" />
      <rect x={gateX + gateW + 4} y={gateY - 22} width="18" height={gateH + 62} fill="#333" stroke="#111" strokeWidth="2" />
      {isSliding && (
        <>
          <path d={`M${gateX - 28} ${gateY + gateH + 22}H${gateX + gateW + 82}`} stroke="#151515" strokeWidth="8" strokeLinecap="round" />
          <path d={`M${gateX - 28} ${gateY + gateH + 22}H${gateX + gateW + 82}`} stroke="#F2B90D" strokeWidth="3" strokeLinecap="round" />
          <circle cx={gateX + gateW * 0.24} cy={gateY + gateH + 16} r="9" fill="#1F1F1F" stroke="#F2B90D" strokeWidth="2" />
          <circle cx={gateX + gateW * 0.76} cy={gateY + gateH + 16} r="9" fill="#1F1F1F" stroke="#F2B90D" strokeWidth="2" />
        </>
      )}
      {isSwing && (
        <>
          <circle cx={gateX - 4} cy={gateY + 42} r="5" fill="#F2B90D" />
          <circle cx={gateX - 4} cy={gateY + gateH - 42} r="5" fill="#F2B90D" />
          <circle cx={gateX + gateW + 4} cy={gateY + 42} r="5" fill="#F2B90D" />
          <circle cx={gateX + gateW + 4} cy={gateY + gateH - 42} r="5" fill="#F2B90D" />
        </>
      )}

      <rect x={gateX} y={gateY} width={gateW} height={gateH} fill={color} stroke="#151515" strokeWidth="6" />
      <rect x={gateX + 16} y={gateY + 16} width={gateW - 32} height={gateH - 32} fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="3" />
      <path d={`M${gateX + 18} ${gateY + gateH / 2}H${gateX + gateW - 18}`} stroke="rgba(255,255,255,0.35)" strokeWidth="2" />
      {Array.from({ length: leafCount - 1 }).map((_, i) => (
        <path key={i} d={`M${gateX + leafW * (i + 1)} ${gateY}V${gateY + gateH}`} stroke="#E2E2E2" strokeWidth="3" />
      ))}
      {form.fill.includes("chapa") && (
        <g opacity="0.9">
          {Array.from({ length: leafCount }).map((_, i) => (
            <g key={i}>
              <rect x={gateX + leafW * i + 26} y={gateY + 26} width={leafW - 52} height={gateH - 52} fill="rgba(255,255,255,0.08)" stroke="rgba(255,255,255,0.38)" />
              {Array.from({ length: 3 }).map((_, line) => (
                <path key={line} d={`M${gateX + leafW * i + 34} ${gateY + 58 + line * 34}H${gateX + leafW * (i + 1) - 34}`} stroke="rgba(255,255,255,0.35)" strokeWidth="2" />
              ))}
            </g>
          ))}
        </g>
      )}
      {barCount > 0 && (
        <g>
          {Array.from({ length: barCount }).map((_, i) => {
            const x = gateX + 26 + i * ((gateW - 52) / (barCount - 1));
            return <rect key={i} x={x} y={gateY + 14} width="8" height={gateH - 28} fill="#DADADA" opacity="0.85" />;
          })}
        </g>
      )}
      {form.fill === "malla" && (
        <g stroke="rgba(255,255,255,0.35)" strokeWidth="1">
          {Array.from({ length: 12 }).map((_, i) => (
            <path key={`d1-${i}`} d={`M${gateX + i * 46} ${gateY + gateH}L${gateX + i * 46 + 160} ${gateY}`} />
          ))}
          {Array.from({ length: 12 }).map((_, i) => (
            <path key={`d2-${i}`} d={`M${gateX + i * 46} ${gateY}L${gateX + i * 46 + 160} ${gateY + gateH}`} />
          ))}
        </g>
      )}
      {form.fill === "madera" && (
        <g>
          {Array.from({ length: 9 }).map((_, i) => (
            <rect key={i} x={gateX + 14 + i * 53} y={gateY + 16} width="34" height={gateH - 32} fill={i % 2 ? "#7B5235" : "#91613E"} />
          ))}
        </g>
      )}
      {isLift && (
        <>
          <path d={`M${gateX + 26} ${gateY + gateH - 20}L${gateX + gateW - 26} ${gateY + 20}`} stroke="rgba(255,255,255,0.45)" strokeWidth="3" strokeDasharray="8 5" />
          <path d={`M${gateX - 34} ${gateY - 10}V${gateY + gateH + 32}`} stroke="#F2B90D" strokeWidth="3" />
          <path d={`M${gateX + gateW + 36} ${gateY - 10}V${gateY + gateH + 32}`} stroke="#F2B90D" strokeWidth="3" />
        </>
      )}

      <path d={`M${gateX} ${gateY - 18}H${gateX + gateW}`} stroke="#151515" strokeWidth="2" markerStart="url(#arrow)" markerEnd="url(#arrow)" />
      <path d={`M${gateX + gateW + 38} ${gateY}V${gateY + gateH}`} stroke="#151515" strokeWidth="2" markerStart="url(#arrow)" markerEnd="url(#arrow)" />
      <rect x={gateX + gateW / 2 - 54} y={gateY - 36} width="108" height="18" fill="#F3F1EA" stroke="#151515" />
      <text x={gateX + gateW / 2} y={gateY - 23} fill="#151515" fontSize="12" fontWeight="700" textAnchor="middle" fontFamily="JetBrains Mono">{fmtMm(form.width)}</text>
      <rect x={gateX + gateW + 46} y={gateY + gateH / 2 - 49} width="18" height="98" fill="#F3F1EA" stroke="#151515" />
      <text x={gateX + gateW + 59} y={gateY + gateH / 2} fill="#151515" fontSize="12" fontWeight="700" textAnchor="middle" fontFamily="JetBrains Mono" transform={`rotate(90 ${gateX + gateW + 59} ${gateY + gateH / 2})`}>{fmtMm(form.height)}</text>
      <rect x="32" y="266" width="556" height="18" fill="#151515" />
      <text x="42" y="279" fill="#F2B90D" fontSize="10" fontFamily="Inter">Vista orientativa para cotizacion · validar vano, nivel de piso y anclajes antes de fabricar</text>
    </svg>
  );
}

export default function PortonesIA() {
  const [form, setForm] = useState(defaultForm);
  const [rawRequest, setRawRequest] = useState("");
  const [history, setHistory] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [selectedInquiryId, setSelectedInquiryId] = useState("");
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [clientDialogOpen, setClientDialogOpen] = useState(false);
  const [clientDraft, setClientDraft] = useState(emptyClientDraft);
  const [saved, setSaved] = useState([]);
  const [priceMaterials, setPriceMaterials] = useState([]);
  const [selectedMaterialId, setSelectedMaterialId] = useState("");
  const [materialSearch, setMaterialSearch] = useState("");
  const [budgetItems, setBudgetItems] = useState([]);
  const [laborCost, setLaborCost] = useState(0);
  const [marginPct, setMarginPct] = useState(30);
  const [savingQuote, setSavingQuote] = useState(false);
  const [assigningClient, setAssigningClient] = useState(false);
  const [sendingQuote, setSendingQuote] = useState(false);
  const [savedQuote, setSavedQuote] = useState(null);
  const [creatingOrder, setCreatingOrder] = useState(false);
  const [createdOrder, setCreatedOrder] = useState(null);
  const [creatingCutPlan, setCreatingCutPlan] = useState(false);
  const [cutPlanResult, setCutPlanResult] = useState(null);
  const [createdResponse, setCreatedResponse] = useState("");
  const [generatedAt, setGeneratedAt] = useState("");
  const [sendingTelegram, setSendingTelegram] = useState(false);
  const [telegramStatus, setTelegramStatus] = useState("");
  const responseRef = useRef(null);
  const planRef = useRef(null);
  const cuts = useMemo(() => buildCuts(form), [form]);
  const materials = useMemo(() => buildMaterials(form, cuts), [form, cuts]);
  const budgetText = useMemo(() => buildBudgetText(budgetItems, laborCost, marginPct), [budgetItems, laborCost, marginPct]);
  const budget = useMemo(() => budgetTotals(budgetItems, laborCost, marginPct), [budgetItems, laborCost, marginPct]);
  const filteredPriceMaterials = useMemo(() => {
    const term = materialSearch.trim().toLowerCase();
    const list = term
      ? priceMaterials.filter((material) => {
          const haystack = [material.name, material.category, material.source_group, material.source_code, material.unit]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          return haystack.includes(term);
        })
      : priceMaterials;
    return list.slice(0, 8);
  }, [priceMaterials, materialSearch]);
  const aiPackage = useMemo(() => {
    const base = buildAiPackage(form, cuts, materials);
    return budgetText ? `${base}\n\n${budgetText}` : base;
  }, [form, cuts, materials, budgetText]);

  const set = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));

  const loadHistory = () => {
    api.get("/portonesia/jobs?limit=30").then((r) => setHistory(r.data || [])).catch(() => {});
  };

  useEffect(() => {
    loadHistory();
    api.get("/materials").then((r) => setPriceMaterials(r.data || [])).catch(() => {});
    api.get("/inquiries").then((r) => setInquiries(r.data || [])).catch(() => {});
    api.get("/clients").then((r) => setClients(r.data || [])).catch(() => {});
  }, []);

  const loadInquiry = (inquiryId) => {
    setSelectedInquiryId(inquiryId);
    const inquiry = inquiries.find((item) => item.id === inquiryId);
    if (!inquiry) return;
    const text = inquiry.message || "";
    const parsed = parseCustomerRequest(text);
    setRawRequest(text);
    setForm((current) => ({
      ...current,
      ...parsed,
      client: inquiry.name || current.client,
      phone: inquiry.phone || current.phone,
      location: inquiry.service_type || current.location,
    }));
    const digits = String(inquiry.phone || "").replace(/\D/g, "");
    const matchedClient = clients.find((client) => client.id === inquiry.client_id)
      || (digits ? clients.find((client) => String(client.phone || "").replace(/\D/g, "").endsWith(digits.slice(-8))) : null);
    setSelectedClientId(matchedClient?.id || "");
    setCreatedResponse("");
    setGeneratedAt("");
    setTelegramStatus("");
    setSavedQuote(null);
    setCreatedOrder(null);
    setCutPlanResult(null);
    toast.success("Consulta cargada");
  };

  const selectClient = (clientId) => {
    setSelectedClientId(clientId);
    const client = clients.find((item) => item.id === clientId);
    if (!client) return;
    setForm((current) => ({
      ...current,
      client: client.name || current.client,
      phone: client.phone || current.phone,
      location: client.address || current.location,
    }));
    if (selectedInquiryId) {
      api.patch(`/inquiries/${selectedInquiryId}/client`, { client_id: client.id }).catch(() => {});
    }
    setCreatedResponse("");
    resetProductionFlow();
  };

  const openClientDialog = () => {
    const inquiry = inquiries.find((item) => item.id === selectedInquiryId);
    setClientDraft({
      name: inquiry?.name || form.client || "",
      phone: inquiry?.phone || form.phone || "",
      email: inquiry?.email || "",
      address: form.location || "",
      notes: rawRequest ? `Consulta: ${rawRequest.slice(0, 220)}` : "",
    });
    setClientDialogOpen(true);
  };

  const saveClientDraft = async () => {
    if (!clientDraft.name.trim()) {
      toast.warning("Cargá el nombre del cliente");
      return;
    }
    setAssigningClient(true);
    try {
      const { data } = await api.post("/clients", {
        name: clientDraft.name.trim(),
        phone: clientDraft.phone.trim(),
        email: clientDraft.email.trim(),
        address: clientDraft.address.trim(),
        notes: clientDraft.notes.trim(),
      });
      setSelectedClientId(data.id);
      setClients((current) => {
        const exists = current.some((item) => item.id === data.id);
        return exists ? current.map((item) => (item.id === data.id ? data : item)) : [data, ...current];
      });
      setForm((current) => ({
        ...current,
        client: data.name || current.client,
        phone: data.phone || current.phone,
        location: data.address || current.location,
      }));
      if (selectedInquiryId) await api.patch(`/inquiries/${selectedInquiryId}/client`, { client_id: data.id }).catch(() => {});
      setClientDialogOpen(false);
      toast.success(`Cliente asignado: ${data.name}`);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setAssigningClient(false);
    }
  };

  const resetProductionFlow = () => {
    setSavedQuote(null);
    setCreatedOrder(null);
    setCutPlanResult(null);
  };

  const addBudgetMaterial = (materialId) => {
    const material = priceMaterials.find((item) => item.id === materialId);
    if (!material) return;
    setBudgetItems((current) => [
      ...current,
      {
        material_id: material.id,
        description: `${material.name} (${material.unit || "unidad"})`,
        quantity: 1,
        unit_price: Number(material.price || 0),
        source: material.source || "",
        source_code: material.source_code || "",
      },
    ]);
    setSelectedMaterialId("");
    setMaterialSearch("");
    setCreatedResponse("");
    resetProductionFlow();
  };

  const setBudgetItem = (index, key, value) => {
    setBudgetItems((current) => current.map((item, itemIndex) => (itemIndex === index ? { ...item, [key]: value } : item)));
    setCreatedResponse("");
    resetProductionFlow();
  };

  const removeBudgetItem = (index) => {
    setBudgetItems((current) => current.filter((_, itemIndex) => itemIndex !== index));
    setCreatedResponse("");
    resetProductionFlow();
  };

  const interpretRequest = () => {
    const parsed = parseCustomerRequest(rawRequest);
    if (Object.keys(parsed).length === 0) {
    toast.warning("Pegá una solicitud para interpretar");
      return;
    }
    setForm((current) => ({ ...current, ...parsed }));
    setCreatedResponse("");
    setGeneratedAt("");
    setTelegramStatus("");
    toast.success("Solicitud interpretada y cargada");
  };

  const loadImportedExample = (text) => {
    const parsed = parseCustomerRequest(text);
    setRawRequest(text);
    setForm((current) => ({ ...current, ...parsed }));
    setCreatedResponse("");
    setGeneratedAt("");
    setTelegramStatus("");
    toast.success("Ejemplo importado cargado");
  };

  const saveDraft = () => {
    const item = { ...form, savedAt: new Date().toISOString() };
    const next = [item, ...saved].slice(0, 5);
    setSaved(next);
    localStorage.setItem("gr_portonesia_drafts", JSON.stringify(next));
    toast.success("Borrador guardado en este navegador");
  };

  const copyPackage = async () => {
    await navigator.clipboard.writeText(aiPackage);
    toast.success("Detalle tecnico copiado");
  };

  const downloadPlanImage = async () => {
    const svg = planRef.current?.querySelector("svg");
    if (!svg) {
      toast.error("No encontre el plano para descargar");
      return;
    }
    const serialized = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([serialized], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = 1240;
      canvas.height = 620;
      const context = canvas.getContext("2d");
      context.fillStyle = "#101010";
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      const link = document.createElement("a");
      link.download = `${form.jobNumber || "porton"}-plano.jpg`;
      link.href = canvas.toDataURL("image/jpeg", 0.92);
      link.click();
      toast.success("Imagen JPG del plano descargada");
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      toast.error("No pude generar la imagen del plano");
    };
    image.src = url;
  };

  const printPdf = () => {
    window.print();
  };

  const createResponse = () => {
    const baseResponse = buildCreatedResponse(form, cuts, materials);
    const response = budgetText ? `${baseResponse}\n\n${budgetText}` : baseResponse;
    setCreatedResponse(response);
    setGeneratedAt(new Date().toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" }));
    localStorage.setItem("gr_portonesia_last_response", JSON.stringify({ id: form.jobNumber, response, createdAt: new Date().toISOString() }));
    setTimeout(() => responseRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    toast.success("Propuesta creada");
  };

  const queueTelegram = async () => {
    setSendingTelegram(true);
    setTelegramStatus("Enviando al encargado...");
    try {
      const colorLabel = COLORS.find((c) => c.value === form.color)?.label || form.color;
      const { data } = await api.post("/portonesia/telegram", {
        job_number: form.jobNumber,
        client: form.client,
        phone: form.phone,
        location: form.location,
        gate_type: form.gateType,
        width: Number(form.width),
        height: Number(form.height),
        leaves: Number(form.leaves),
        fill: form.fill,
        frame_profile: form.frameProfile,
        vertical_profile: form.verticalProfile,
        column_profile: form.columnProfile,
        automation: form.automation,
        color_label: colorLabel,
        finish: form.finish,
        notes: form.notes,
        ai_package: aiPackage,
        cuts,
        materials,
        budget_items: budgetItems.map((item) => ({
          ...item,
          quantity: Number(item.quantity || 0),
          unit_price: Number(item.unit_price || 0),
        })),
        budget_labor_cost: Number(laborCost || 0),
        budget_margin_pct: Number(marginPct || 0),
      });
      setCreatedResponse(data.response_text);
      setGeneratedAt(new Date().toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" }));
      const requestMsg = data.telegram?.message_id ? `solicitud #${data.telegram.message_id}` : "solicitud sin confirmar";
      const responseMsg = data.telegram_response?.message_id ? `propuesta #${data.telegram_response.message_id}` : "propuesta sin confirmar";
      const planMsg = data.telegram_plan?.message_id ? `plano #${data.telegram_plan.message_id}` : "plano sin confirmar";
      setTelegramStatus(
        data.telegram?.sent || data.telegram_response?.sent || data.telegram_plan?.sent
          ? `Enviado al encargado. Registro ${data.id}. Incluye ${requestMsg}, ${responseMsg} y ${planMsg}.`
          : `Trabajo creado, pero no se confirmó el envío: ${data.telegram?.reason || data.telegram_response?.reason || data.telegram_plan?.reason || "sin detalle"}`
      );
      setTimeout(() => responseRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
      loadHistory();
      if (data.telegram?.sent) toast.success("Enviado al encargado");
      else toast.warning("Se creó el trabajo, pero no se confirmó envío");
    } catch (err) {
      setTelegramStatus("Error al enviar trabajo");
      toast.error(apiError(err));
    } finally {
      setSendingTelegram(false);
    }
  };

  const saveQuoteFromBudget = async () => {
    if (!budgetItems.length) {
      toast.warning("Agregá materiales al presupuesto primero");
      return;
    }
    if (!selectedClientId) {
      toast.warning("Elegí o creá un cliente antes de guardar la cotización");
      return;
    }
    if (!localStorage.getItem("gr_token")) {
      toast.error("Para guardar cotización, primero iniciá sesión en el panel en este navegador");
      return;
    }
    setSavingQuote(true);
    try {
      const { data } = await api.post("/quotes", {
        client_id: selectedClientId || null,
        client_name: form.client || "Cliente sin nombre",
        inquiry_id: selectedInquiryId || null,
        title: `${form.jobNumber} - Porton ${form.gateType} ${mmToMeters(form.width)} x ${mmToMeters(form.height)}`,
        items: budgetItems.map((item) => ({
          description: item.description,
          quantity: Number(item.quantity || 0),
          unit_price: Number(item.unit_price || 0),
        })),
        labor_cost: Number(laborCost || 0),
        margin_pct: Number(marginPct || 0),
        plan_payload: {
          job_number: form.jobNumber,
          client: form.client,
          phone: form.phone,
          location: form.location,
          gate_type: form.gateType,
          width: Number(form.width),
          height: Number(form.height),
          leaves: Number(form.leaves),
          fill: form.fill,
          frame_profile: form.frameProfile,
          vertical_profile: form.verticalProfile,
          column_profile: form.columnProfile,
          automation: form.automation,
          color_label: COLORS.find((c) => c.value === form.color)?.label || form.color,
          finish: form.finish,
          notes: form.notes,
          cuts,
          materials,
        },
      });
      setSavedQuote(data);
      setCreatedOrder(null);
      setCutPlanResult(null);
      toast.success(`Cotización ${data.number} guardada`);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSavingQuote(false);
    }
  };

  const sendQuoteToClient = async () => {
    if (!savedQuote?.id) {
      toast.warning("Primero guardá la cotización");
      return;
    }
    const client = clients.find((item) => item.id === selectedClientId);
    const phone = client?.phone || form.phone;
    if (!phone) {
      toast.warning("El cliente no tiene teléfono cargado");
      return;
    }
    setSendingQuote(true);
    try {
      await api.post(`/quotes/${savedQuote.id}/send-pdf`, {
        phone,
        message: `Hola ${form.client || savedQuote.client_name}! Te enviamos la cotización ${savedQuote.number} por ${fmtARS(savedQuote.total)}. GR Soluciones Integrales.`,
        client_id: selectedClientId || null,
      });
      toast.success("Cotización enviada al cliente");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSendingQuote(false);
    }
  };

  const createWorkOrderFromQuote = async () => {
    if (!savedQuote?.id) {
      toast.warning("Primero guardá la cotización");
      return;
    }
    setCreatingOrder(true);
    try {
      const { data } = await api.post(`/quotes/${savedQuote.id}/to-order`);
      setCreatedOrder(data);
      setCutPlanResult(null);
      toast.success(`Orden ${data.number} creada`);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setCreatingOrder(false);
    }
  };

  const createCutPlanFromCuts = async () => {
    if (!createdOrder?.id) {
      toast.warning("Primero creá la orden de trabajo");
      return;
    }
    const pieces = cuts
      .filter((item) => Number(item.length) > 0 && Number(item.quantity) > 0)
      .filter((item) => !String(item.label || "").toLowerCase().startsWith("paño"))
      .map((item) => ({ label: item.label, length: Number(item.length), quantity: Number(item.quantity) }));
    if (!pieces.length) {
      toast.warning("No hay cortes válidos para generar orden de corte");
      return;
    }
    setCreatingCutPlan(true);
    try {
      const { data } = await api.post("/cuts/optimize", {
        stock_length: 6000,
        kerf: 3,
        pieces,
        save: true,
        name: `${form.jobNumber} - ${createdOrder.number}`,
        work_order_id: createdOrder.id,
      });
      setCutPlanResult(data);
      toast.success(`Orden de corte guardada: ${data.total_bars} barras`);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setCreatingCutPlan(false);
    }
  };

  const loadJob = (job) => {
    const color = COLORS.find((item) => item.label === job.color_label)?.value || defaultForm.color;
    setForm({
      jobNumber: job.job_number || defaultForm.jobNumber,
      client: job.client || "",
      phone: job.phone || "",
      location: job.location || "",
      gateType: job.gate_type || defaultForm.gateType,
      width: job.width || defaultForm.width,
      height: job.height || defaultForm.height,
      leaves: job.leaves || defaultForm.leaves,
      fill: job.fill || defaultForm.fill,
      frameProfile: job.frame_profile || defaultForm.frameProfile,
      verticalProfile: job.vertical_profile || defaultForm.verticalProfile,
      columnProfile: job.column_profile || defaultForm.columnProfile,
      automation: job.automation || defaultForm.automation,
      color,
      finish: job.finish || defaultForm.finish,
      notes: job.notes || "",
    });
    setBudgetItems(job.budget_items || []);
    setLaborCost(job.budget_labor_cost || 0);
    setMarginPct(job.budget_margin_pct ?? 30);
    setCreatedResponse(job.response_text || "");
    setGeneratedAt(job.created_at ? new Date(job.created_at).toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" }) : "");
    setTelegramStatus(job.telegram_plan?.message_id ? `Trabajo cargado del historial. Plano enviado #${job.telegram_plan.message_id}` : "Trabajo cargado del historial");
    setTimeout(() => responseRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  const removeJob = async (event, job) => {
    event.stopPropagation();
    if (!localStorage.getItem("gr_token")) {
      toast.error("Para eliminar historial, primero inicia sesion en el panel en este navegador");
      return;
    }
    if (!window.confirm(`Eliminar el trabajo ${job.job_number || job.id} del historial?`)) return;
    try {
      await api.delete(`/portonesia/jobs/${job.id}`);
      loadHistory();
      toast.success("Trabajo eliminado");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const responsePreview = createdResponse || (budgetText ? `${buildCreatedResponse(form, cuts, materials)}\n\n${budgetText}` : buildCreatedResponse(form, cuts, materials));

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <header className="border-b border-neutral-800 bg-[#111] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3 min-w-0">
            <img src="/logo.webp" alt="GR" className="h-10 w-10 object-contain rounded-sm bg-white p-0.5" />
            <div className="min-w-0">
              <p className="font-heading font-extrabold uppercase tracking-tight text-sm leading-tight truncate">GR Portones</p>
              <p className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 truncate">Consulta · cotizacion · produccion</p>
            </div>
          </Link>
          <Link to="/" className="text-xs uppercase tracking-wider text-neutral-400 hover:text-white flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" /> Volver
          </Link>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 lg:py-8">
        <section className="mb-6">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#F2B90D] text-black text-[11px] font-bold uppercase tracking-[0.18em] px-3 py-1.5 mb-4">
                <Sparkles className="w-3.5 h-3.5" /> Flujo de trabajo
              </div>
              <h1 className="text-3xl sm:text-4xl font-heading font-extrabold uppercase tracking-tight leading-none">
                Portones
              </h1>
              <p className="mt-3 text-sm text-neutral-400 max-w-2xl">
                Convertí una consulta en cotización con plano, presupuesto, orden de trabajo y orden de corte.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={createResponse} className="bg-[#F2B90D] hover:bg-[#DFA90C] text-black rounded-sm font-heading font-bold uppercase tracking-wider h-11 px-5">
                <Sparkles className="w-4 h-4" /> Generar propuesta
              </Button>
              <Button onClick={queueTelegram} disabled={sendingTelegram} variant="outline" className="border-[#F2B90D] text-[#F2B90D] rounded-sm hover:bg-[#F2B90D]/10 hover:text-[#F2B90D] h-11 px-5">
                <Send className="w-4 h-4" /> {sendingTelegram ? "Enviando..." : "Enviar al encargado"}
              </Button>
              <Button onClick={saveDraft} variant="outline" className="border-neutral-700 rounded-sm hover:bg-neutral-800 hover:text-white">
                <Save className="w-4 h-4" /> Borrador
              </Button>
            </div>
          </div>
        </section>

        <section className="mb-6 grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {FLOW_STEPS.map((step) => {
            const value = {
              "1": selectedInquiryId || rawRequest ? "lista" : "pendiente",
              "2": `${mmToMeters(form.width)} x ${mmToMeters(form.height)}`,
              "3": savedQuote?.number || (budgetItems.length ? fmtARS(budget.total) : "pendiente"),
              "4": createdOrder?.number || "pendiente",
              "5": cutPlanResult?.plan_id ? `${cutPlanResult.total_bars} barras` : "pendiente",
            }[step.number];
            const done = value !== "pendiente";
            return (
              <div key={step.number} className={`border rounded-md p-3 ${done ? "bg-[#171717] border-[#F2B90D]/50" : "bg-[#141414] border-neutral-800"}`}>
                <div className="flex items-start gap-3">
                  <span className={`h-8 w-8 shrink-0 rounded-sm grid place-items-center font-heading font-black ${done ? "bg-[#F2B90D] text-black" : "bg-neutral-900 text-neutral-500"}`}>
                    {step.number}
                  </span>
                  <div className="min-w-0">
                    <p className="font-heading font-bold uppercase text-sm">{step.title}</p>
                    <p className="text-xs text-neutral-500 leading-snug mt-1">{step.detail}</p>
                    <p className={`font-mono text-xs mt-2 ${done ? "text-[#F2B90D]" : "text-neutral-500"}`}>{value}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </section>

        <section className="mb-6 bg-[#141414] border border-[#F2B90D]/40 rounded-md overflow-hidden">
          <div className="px-4 py-3 border-b border-neutral-800 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#F2B90D] font-bold">Paso 1</p>
              <h2 className="font-heading font-bold uppercase text-xl">Consulta</h2>
            </div>
            <Button onClick={interpretRequest} className="bg-[#F2B90D] hover:bg-[#DFA90C] text-black rounded-sm font-heading font-bold uppercase tracking-wider h-11 px-5">
              <Sparkles className="w-4 h-4" /> Interpretar consulta
            </Button>
          </div>
          <div className="p-4 grid lg:grid-cols-[1fr_280px] gap-4">
            <Textarea
              value={rawRequest}
              onChange={(event) => setRawRequest(event.target.value)}
              placeholder="Ejemplo: Porton corredizo negro de 4,20 x 2,10, chapa plegada, con motor, para frente de casa. Algo simple y resistente."
              className="min-h-[118px] bg-neutral-950 border-neutral-800 rounded-sm text-white resize-y"
            />
            <div className="bg-neutral-950 border border-neutral-800 rounded-sm p-4 text-sm text-neutral-400">
              <p className="font-heading font-bold uppercase text-white mb-2">Atajo</p>
              <p>Elegí una consulta guardada, pegá una nueva o cargá un ejemplo real.</p>
            </div>
          </div>
          {inquiries.length > 0 && (
            <div className="px-4 pb-4 grid lg:grid-cols-2 gap-3">
              <div>
                <label className={LABEL_CLASS}>Cargar consulta existente</label>
                <select value={selectedInquiryId} onChange={(event) => loadInquiry(event.target.value)} className={SELECT_CLASS}>
                  <option value="">Elegir consulta guardada</option>
                  {inquiries.map((inquiry) => (
                    <option key={inquiry.id} value={inquiry.id}>
                      {inquiry.tracking_code || "Consulta"} · {inquiry.name} · {inquiry.service_type}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={LABEL_CLASS}>Cliente asignado</label>
                <div className="grid sm:grid-cols-[1fr_auto] gap-2">
                  <select value={selectedClientId} onChange={(event) => selectClient(event.target.value)} className={SELECT_CLASS}>
                    <option value="">Elegir cliente</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.name} {client.phone ? `· ${client.phone}` : ""}
                      </option>
                    ))}
                  </select>
                  <Button
                    type="button"
                    onClick={openClientDialog}
                    disabled={assigningClient}
                    variant="outline"
                    className="border-neutral-700 rounded-sm hover:bg-neutral-800 hover:text-white"
                  >
                    <User className="w-4 h-4" /> Crear cliente
                  </Button>
                </div>
                {selectedClientId && (
                  <p className="mt-2 text-xs text-[#F2B90D] font-mono">
                    Asignado: {clients.find((client) => client.id === selectedClientId)?.name || form.client}
                  </p>
                )}
              </div>
            </div>
          )}
          <div className="px-4 pb-4 flex flex-wrap gap-2">
            {IMPORTED_EXAMPLES.map((example) => (
              <Button
                key={example.label}
                type="button"
                size="sm"
                variant="outline"
                onClick={() => loadImportedExample(example.text)}
                className="border-neutral-700 rounded-sm hover:bg-neutral-800 hover:text-white"
              >
                {example.label}
              </Button>
            ))}
          </div>
        </section>

        <section ref={responseRef} className="mb-6 grid xl:grid-cols-[1.05fr_0.95fr] gap-6">
          <div className="bg-[#141414] border border-[#F2B90D]/40 rounded-md overflow-hidden">
            <div className="px-4 py-3 border-b border-neutral-800 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#F2B90D] font-bold">Paso 2</p>
                <h2 className="font-heading font-bold uppercase text-xl">Cotización visual</h2>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-xs text-neutral-500">{mmToMeters(form.width)} x {mmToMeters(form.height)}</span>
                <Button size="sm" variant="outline" onClick={downloadPlanImage} className="border-neutral-700 rounded-sm hover:bg-neutral-800 hover:text-white">
                  <Download className="w-3.5 h-3.5" /> Generar imagen
                </Button>
                <Button size="sm" variant="outline" onClick={printPdf} className="border-neutral-700 rounded-sm hover:bg-neutral-800 hover:text-white">
                  <Printer className="w-3.5 h-3.5" /> PDF
                </Button>
              </div>
            </div>
            <div ref={planRef} className="aspect-[16/9] min-h-[260px] bg-neutral-950">
              <GatePreview form={form} />
            </div>
            <div className="px-4 py-4 border-t border-neutral-800 grid sm:grid-cols-3 gap-3">
              <div className="bg-neutral-950 border border-neutral-800 rounded-sm p-3">
                <p className="text-[10px] uppercase tracking-wider text-neutral-500">Tipo</p>
                <p className="font-heading font-bold uppercase text-sm">{form.gateType}</p>
              </div>
              <div className="bg-neutral-950 border border-neutral-800 rounded-sm p-3">
                <p className="text-[10px] uppercase tracking-wider text-neutral-500">Relleno</p>
                <p className="font-heading font-bold uppercase text-sm">{form.fill}</p>
              </div>
              <div className="bg-neutral-950 border border-neutral-800 rounded-sm p-3">
                <p className="text-[10px] uppercase tracking-wider text-neutral-500">Color</p>
                <div className="mt-1 flex items-center gap-2">
                  <span className="h-4 w-4 rounded-sm border border-neutral-700" style={{ background: form.color }} />
                  <p className="font-heading font-bold uppercase text-sm">{COLORS.find((c) => c.value === form.color)?.label || form.color}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-[#141414] border border-[#F2B90D]/40 rounded-md overflow-hidden">
            <div className="px-4 py-3 border-b border-neutral-800 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#F2B90D] font-bold">Propuesta</p>
                <h2 className="font-heading font-bold uppercase text-xl">Texto para cliente</h2>
                {generatedAt && <span className="font-mono text-xs text-neutral-500">Actualizado {generatedAt}</span>}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" onClick={createResponse} className="bg-[#F2B90D] hover:bg-[#DFA90C] text-black rounded-sm font-heading font-bold uppercase">
                  Generar propuesta
                </Button>
                <Button size="sm" variant="outline" onClick={queueTelegram} disabled={sendingTelegram} className="border-neutral-700 rounded-sm hover:bg-neutral-800 hover:text-white">
                  <Send className="w-3.5 h-3.5" /> {sendingTelegram ? "Enviando" : "Enviar al encargado"}
                </Button>
              </div>
            </div>
            <Textarea
              value={responsePreview}
              onChange={(event) => {
                setCreatedResponse(event.target.value);
                setGeneratedAt("editado");
              }}
              className="min-h-[360px] bg-neutral-950 border-0 rounded-none text-sm text-neutral-200 resize-y leading-relaxed"
            />
            <div className="px-4 py-3 border-t border-neutral-800 flex flex-wrap gap-2 bg-neutral-950">
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  await navigator.clipboard.writeText(responsePreview);
                  toast.success("Propuesta copiada");
                }}
                className="border-neutral-700 rounded-sm hover:bg-neutral-800 hover:text-white"
              >
                <Clipboard className="w-3.5 h-3.5" /> Copiar propuesta
              </Button>
              <Button size="sm" variant="outline" onClick={copyPackage} className="border-neutral-700 rounded-sm hover:bg-neutral-800 hover:text-white">
                <Clipboard className="w-3.5 h-3.5" /> Copiar detalle tecnico
              </Button>
            </div>
            {telegramStatus && (
              <div className="px-4 py-3 border-t border-neutral-800 text-xs font-mono text-[#F2B90D] bg-neutral-950 space-y-2">
                <div>{telegramStatus}</div>
                {telegramStatus.startsWith("Enviado al encargado") && (
                  <span className="inline-flex text-white">
                    Revisá el chat de trabajo del encargado.
                  </span>
                )}
              </div>
            )}
          </div>
        </section>

        <details className="mb-6 bg-[#141414] border border-neutral-800 rounded-md overflow-hidden">
          <summary className="px-4 py-3 border-b border-neutral-800 cursor-pointer list-none">
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold">Historial</p>
              <h2 className="font-heading font-bold uppercase text-xl">Trabajos guardados</h2>
            </div>
          </summary>
          <div className="px-4 py-3 border-b border-neutral-800">
            <Button size="sm" variant="outline" onClick={loadHistory} className="border-neutral-700 rounded-sm hover:bg-neutral-800 hover:text-white">
              Actualizar historial
            </Button>
          </div>
          {history.length === 0 ? (
            <p className="px-4 py-4 text-sm text-neutral-500">Todavia no hay trabajos guardados.</p>
          ) : (
            <div className="divide-y divide-neutral-800">
              {history.slice(0, 8).map((job) => (
                <div
                  key={job.id}
                  onClick={() => loadJob(job)}
                  className="w-full px-4 py-3 text-left hover:bg-neutral-900 transition-colors grid md:grid-cols-[1fr_180px_130px_40px] gap-2 items-center cursor-pointer"
                >
                  <div className="min-w-0">
                    <p className="font-heading font-bold uppercase text-sm truncate">{job.job_number || "Sin numero"} · {job.client || "Sin cliente"}</p>
                    <p className="text-xs text-neutral-500 truncate">{job.gate_type || "porton"} · {job.width || "-"} x {job.height || "-"} mm · {job.fill || "sin relleno"}</p>
                  </div>
                  <p className="text-xs text-neutral-400">{job.created_at ? new Date(job.created_at).toLocaleString("es-AR") : ""}</p>
                  <p className={`text-[10px] uppercase font-bold ${job.telegram_plan?.sent ? "text-[#F2B90D]" : "text-neutral-500"}`}>
                    {job.telegram_plan?.sent ? `Plano #${job.telegram_plan.message_id}` : "Sin plano"}
                  </p>
                  <button
                    type="button"
                    onClick={(event) => removeJob(event, job)}
                    className="justify-self-end text-red-400 hover:bg-red-500/10 p-2 rounded-sm"
                    title="Eliminar del historial"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </details>

        <div className="grid xl:grid-cols-[390px_1fr] gap-6">
          <section className="bg-[#141414] border border-neutral-800 rounded-md p-4 sm:p-5 space-y-5">
            <div>
              <h2 className="font-heading font-bold uppercase text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#F2B90D]" /> Trabajo
              </h2>
              <div className="grid sm:grid-cols-2 xl:grid-cols-1 gap-3 mt-4">
                <div>
                  <label className={LABEL_CLASS}>Numero</label>
                  <Input value={form.jobNumber} onChange={set("jobNumber")} className={FIELD_CLASS} />
                </div>
                <div>
                  <label className={LABEL_CLASS}>Cliente</label>
                  <Input value={form.client} onChange={set("client")} placeholder="Nombre del cliente" className={FIELD_CLASS} />
                </div>
                <div>
                  <label className={LABEL_CLASS}>Telefono</label>
                  <Input value={form.phone} onChange={set("phone")} placeholder="Telefono del cliente" className={FIELD_CLASS} />
                </div>
                <div>
                  <label className={LABEL_CLASS}>Ubicacion</label>
                  <Input value={form.location} onChange={set("location")} placeholder="Barrio / ciudad / obra" className={FIELD_CLASS} />
                </div>
              </div>
            </div>

            <div>
              <h2 className="font-heading font-bold uppercase text-lg flex items-center gap-2">
                <Ruler className="w-5 h-5 text-[#F2B90D]" /> Medidas
              </h2>
              <div className="grid grid-cols-2 gap-3 mt-4">
                <div>
                  <label className={LABEL_CLASS}>Ancho mm</label>
                  <Input type="number" value={form.width} onChange={set("width")} className={FIELD_CLASS} />
                </div>
                <div>
                  <label className={LABEL_CLASS}>Alto mm</label>
                  <Input type="number" value={form.height} onChange={set("height")} className={FIELD_CLASS} />
                </div>
                <div>
                  <label className={LABEL_CLASS}>Tipo</label>
                  <select value={form.gateType} onChange={set("gateType")} className={SELECT_CLASS}>
                    <option value="corredizo">Corredizo</option>
                    <option value="batiente">Batiente</option>
                    <option value="levadizo">Levadizo</option>
                  </select>
                </div>
                <div>
                  <label className={LABEL_CLASS}>Hojas</label>
                  <select value={form.leaves} onChange={set("leaves")} className={SELECT_CLASS}>
                    <option value="1">1 hoja</option>
                    <option value="2">2 hojas</option>
                    <option value="3">3 hojas</option>
                    <option value="4">4 hojas</option>
                  </select>
                </div>
              </div>
            </div>

            <div>
              <h2 className="font-heading font-bold uppercase text-lg flex items-center gap-2">
                <Wrench className="w-5 h-5 text-[#F2B90D]" /> Fabricacion
              </h2>
              <div className="grid gap-3 mt-4">
                <div>
                  <label className={LABEL_CLASS}>Relleno</label>
                  <select value={form.fill} onChange={set("fill")} className={SELECT_CLASS}>
                    <option value="chapa plegada">Chapa plegada</option>
                    <option value="chapa lisa">Chapa lisa</option>
                    <option value="barrotes">Barrotes</option>
                    <option value="malla">Malla</option>
                    <option value="madera">Madera</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={LABEL_CLASS}>Marco</label>
                    <Input value={form.frameProfile} onChange={set("frameProfile")} className={FIELD_CLASS} />
                  </div>
                  <div>
                    <label className={LABEL_CLASS}>Interiores</label>
                    <Input value={form.verticalProfile} onChange={set("verticalProfile")} className={FIELD_CLASS} />
                  </div>
                </div>
                <div>
                  <label className={LABEL_CLASS}>Columnas</label>
                  <Input value={form.columnProfile} onChange={set("columnProfile")} className={FIELD_CLASS} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={LABEL_CLASS}>Automatizacion</label>
                    <select value={form.automation} onChange={set("automation")} className={SELECT_CLASS}>
                      <option value="si">Si</option>
                      <option value="no">No</option>
                      <option value="prever">Prever</option>
                    </select>
                  </div>
                  <div>
                    <label className={LABEL_CLASS}>Terminacion</label>
                    <select value={form.finish} onChange={set("finish")} className={SELECT_CLASS}>
                      <option value="pintura sintetica">Pintura sintetica</option>
                      <option value="epoxi">Epoxi</option>
                      <option value="galvanizado">Galvanizado</option>
                      <option value="sin pintar">Sin pintar</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className={LABEL_CLASS}>Color</label>
                  <div className="grid grid-cols-5 gap-2">
                    {COLORS.map((item) => (
                      <button
                        key={item.value}
                        type="button"
                        onClick={() => setForm((current) => ({ ...current, color: item.value }))}
                        title={item.label}
                        className={`h-10 rounded-sm border ${form.color === item.value ? "border-[#F2B90D] ring-2 ring-[#F2B90D]/30" : "border-neutral-700"}`}
                        style={{ background: item.value }}
                        aria-label={item.label}
                      />
                    ))}
                  </div>
                </div>
                <div>
                  <label className={LABEL_CLASS}>Notas</label>
                  <Textarea value={form.notes} onChange={set("notes")} className="bg-neutral-950 border-neutral-800 rounded-sm min-h-[88px] text-white" />
                </div>
              </div>
            </div>
          </section>

          <section className="space-y-6">
            <div className="grid lg:grid-cols-2 gap-6">
              <div className="bg-[#141414] border border-neutral-800 rounded-md">
                <div className="px-4 py-3 border-b border-neutral-800 flex items-center gap-2">
                  <Wrench className="w-4 h-4 text-[#F2B90D]" />
                  <h2 className="font-heading font-bold uppercase text-lg">Lista de cortes base</h2>
                </div>
                <div className="divide-y divide-neutral-800">
                  {cuts.map((item) => (
                    <div key={`${item.label}-${item.length}`} className="grid grid-cols-[1fr_82px_100px] gap-3 px-4 py-3 text-sm items-center">
                      <span className="text-neutral-200">{item.label}</span>
                      <span className="font-mono text-neutral-400 text-right">{item.quantity} un.</span>
                      <span className="font-mono text-[#F2B90D] text-right">{fmtMm(item.length)}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-[#141414] border border-neutral-800 rounded-md">
                <div className="px-4 py-3 border-b border-neutral-800 flex items-center gap-2">
                  <Package className="w-4 h-4 text-[#F2B90D]" />
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#F2B90D] font-bold">Paso 3</p>
                    <h2 className="font-heading font-bold uppercase text-lg">Presupuesto</h2>
                  </div>
                </div>
                <div className="p-4 space-y-4">
                  <div className="grid sm:grid-cols-[1fr_auto] gap-2">
                    <div className="relative">
                      <Input
                        value={materialSearch}
                        onChange={(event) => {
                          setMaterialSearch(event.target.value);
                          setSelectedMaterialId("");
                        }}
                        placeholder="Buscar material: 60x40, chapa, angulo..."
                        className={FIELD_CLASS}
                      />
                      {materialSearch && (
                        <div className="absolute z-30 mt-1 w-full max-h-72 overflow-y-auto bg-neutral-950 border border-neutral-800 rounded-sm shadow-xl">
                          {filteredPriceMaterials.length === 0 ? (
                            <p className="px-3 py-2 text-xs text-neutral-500">Sin resultados</p>
                          ) : filteredPriceMaterials.map((material) => (
                            <button
                              key={material.id}
                              type="button"
                              onClick={() => {
                                setSelectedMaterialId(material.id);
                                setMaterialSearch(material.name);
                              }}
                              className={`w-full text-left px-3 py-2 text-xs hover:bg-neutral-800 ${selectedMaterialId === material.id ? "bg-[#F2B90D]/10 text-[#F2B90D]" : "text-neutral-200"}`}
                            >
                              <span className="block font-medium truncate">{material.name}</span>
                              <span className="block font-mono text-neutral-500">{fmtARS(material.price)} · {material.unit || "unidad"} {material.source_group ? `· ${material.source_group}` : ""}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <Button
                      type="button"
                      onClick={() => addBudgetMaterial(selectedMaterialId)}
                      disabled={!selectedMaterialId}
                      className="bg-[#F2B90D] hover:bg-[#DFA90C] text-black rounded-sm font-heading uppercase"
                    >
                      Agregar
                    </Button>
                  </div>

                  <div className="space-y-2">
                    {budgetItems.map((item, index) => (
                      <div key={`${item.material_id}-${index}`} className="grid grid-cols-1 sm:grid-cols-[1fr_72px_100px_88px_28px] gap-2 items-center text-sm">
                        <Input
                          value={item.description}
                          onChange={(event) => setBudgetItem(index, "description", event.target.value)}
                          className="bg-neutral-950 border-neutral-800 rounded-sm h-9 text-white"
                        />
                        <Input
                          type="number"
                          value={item.quantity}
                          onChange={(event) => setBudgetItem(index, "quantity", event.target.value)}
                          className="bg-neutral-950 border-neutral-800 rounded-sm h-9 text-white"
                        />
                        <Input
                          type="number"
                          value={item.unit_price}
                          onChange={(event) => setBudgetItem(index, "unit_price", event.target.value)}
                          className="bg-neutral-950 border-neutral-800 rounded-sm h-9 text-white"
                        />
                        <span className="font-mono text-xs text-[#F2B90D] text-right">
                          {fmtARS(Number(item.quantity || 0) * Number(item.unit_price || 0))}
                        </span>
                        <button type="button" onClick={() => removeBudgetItem(index)} className="text-neutral-500 hover:text-red-400">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    {budgetItems.length === 0 && (
                      <p className="text-xs text-neutral-500">Agregá materiales Trotta o propios para calcular el presupuesto real.</p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={LABEL_CLASS}>Mano de obra</label>
                      <Input
                        type="number"
                        value={laborCost}
                        onChange={(event) => { setLaborCost(event.target.value); setCreatedResponse(""); resetProductionFlow(); }}
                        className={FIELD_CLASS}
                      />
                    </div>
                    <div>
                      <label className={LABEL_CLASS}>Margen %</label>
                      <Input
                        type="number"
                        value={marginPct}
                        onChange={(event) => { setMarginPct(event.target.value); setCreatedResponse(""); resetProductionFlow(); }}
                        className={FIELD_CLASS}
                      />
                    </div>
                  </div>

                  <div className="bg-neutral-950 border border-neutral-800 rounded-sm p-4 space-y-1 text-sm">
                    <div className="flex justify-between text-neutral-400">
                      <span>Materiales</span>
                      <span className="font-mono">{fmtARS(budget.materialsSubtotal)}</span>
                    </div>
                    <div className="flex justify-between text-neutral-400">
                      <span>Subtotal + mano de obra</span>
                      <span className="font-mono">{fmtARS(budget.subtotal)}</span>
                    </div>
                    <div className="flex justify-between font-bold text-lg pt-2 border-t border-neutral-800">
                      <span className="font-heading uppercase">Total</span>
                      <span className="font-mono text-[#F2B90D]">{fmtARS(budget.total)}</span>
                    </div>
                  </div>

                  <Button
                    type="button"
                    onClick={saveQuoteFromBudget}
                    disabled={savingQuote || budgetItems.length === 0}
                    className="w-full bg-[#F2B90D] hover:bg-[#DFA90C] text-black rounded-sm font-heading uppercase"
                  >
                    <Save className="w-4 h-4" /> {savingQuote ? "Guardando" : "Guardar cotizacion"}
                  </Button>
                  {savedQuote && (
                    <div className="bg-neutral-950 border border-[#F2B90D]/40 rounded-sm p-3 space-y-3">
                      <div className="flex items-center justify-between gap-3 text-sm">
                        <span className="text-neutral-300">Cotización creada</span>
                        <span className="font-mono text-[#F2B90D]">{savedQuote.number}</span>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-2">
                        <Button
                          type="button"
                          onClick={sendQuoteToClient}
                          disabled={sendingQuote}
                          variant="outline"
                          className="border-neutral-700 rounded-sm hover:bg-neutral-800 hover:text-white font-heading uppercase"
                        >
                          <FileUp className="w-4 h-4" /> {sendingQuote ? "Enviando" : "Enviar al cliente"}
                        </Button>
                        <Link
                          to="/panel/cotizaciones"
                          className="inline-flex items-center justify-center h-10 px-3 rounded-sm border border-neutral-700 text-sm font-heading uppercase hover:bg-neutral-800"
                        >
                          Abrir cotización
                        </Link>
                      </div>
                      <Button
                        type="button"
                        onClick={createWorkOrderFromQuote}
                        disabled={creatingOrder}
                        className="w-full bg-neutral-800 hover:bg-neutral-700 rounded-sm font-heading uppercase"
                      >
                        <ClipboardList className="w-4 h-4" /> {creatingOrder ? "Creando" : "Crear orden de trabajo"}
                      </Button>
                    </div>
                  )}
                  {createdOrder && (
                    <div className="bg-neutral-950 border border-[#F2B90D]/40 rounded-sm p-3 space-y-3">
                      <div className="flex items-center justify-between gap-3 text-sm">
                        <span className="text-neutral-300">Orden de trabajo</span>
                        <span className="font-mono text-[#F2B90D]">{createdOrder.number}</span>
                      </div>
                      <Button
                        type="button"
                        onClick={createCutPlanFromCuts}
                        disabled={creatingCutPlan}
                        className="w-full bg-neutral-800 hover:bg-neutral-700 rounded-sm font-heading uppercase"
                      >
                        <Scissors className="w-4 h-4" /> {creatingCutPlan ? "Calculando" : "Crear orden de corte"}
                      </Button>
                    </div>
                  )}
                  {cutPlanResult?.plan_id && (
                    <div className="bg-neutral-950 border border-green-500/40 rounded-sm p-3 text-sm">
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-neutral-300">Orden de corte guardada</span>
                        <span className="font-mono text-green-400">{cutPlanResult.total_bars} barras</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <details className="bg-[#141414] border border-neutral-800 rounded-md lg:col-span-2 overflow-hidden">
                <summary className="px-4 py-3 border-b border-neutral-800 flex items-center justify-between gap-2 cursor-pointer list-none">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-[#F2B90D]" />
                    <h2 className="font-heading font-bold uppercase text-lg">Detalle tecnico</h2>
                  </div>
                </summary>
                <div className="px-4 py-3 border-b border-neutral-800 flex justify-end">
                  <Button size="sm" variant="outline" onClick={copyPackage} className="border-neutral-700 rounded-sm hover:bg-neutral-800 hover:text-white">
                    <Clipboard className="w-3.5 h-3.5" /> Copiar
                  </Button>
                </div>
                <Textarea readOnly value={aiPackage} className="min-h-[330px] bg-neutral-950 border-0 rounded-none text-xs font-mono text-neutral-300 resize-none" />
              </details>
            </div>
          </section>
        </div>
      </main>
      <Dialog open={clientDialogOpen} onOpenChange={setClientDialogOpen}>
        <DialogContent className="bg-[#171717] border-neutral-800 text-white max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase">Datos del cliente</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className={LABEL_CLASS}>Nombre real</label>
              <Input value={clientDraft.name} onChange={(event) => setClientDraft((current) => ({ ...current, name: event.target.value }))} className={FIELD_CLASS} />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className={LABEL_CLASS}>Telefono</label>
                <Input value={clientDraft.phone} onChange={(event) => setClientDraft((current) => ({ ...current, phone: event.target.value }))} className={FIELD_CLASS} />
              </div>
              <div>
                <label className={LABEL_CLASS}>Email</label>
                <Input value={clientDraft.email} onChange={(event) => setClientDraft((current) => ({ ...current, email: event.target.value }))} className={FIELD_CLASS} />
              </div>
            </div>
            <div>
              <label className={LABEL_CLASS}>Direccion / obra</label>
              <Input value={clientDraft.address} onChange={(event) => setClientDraft((current) => ({ ...current, address: event.target.value }))} className={FIELD_CLASS} />
            </div>
            <div>
              <label className={LABEL_CLASS}>Notas</label>
              <Textarea value={clientDraft.notes} onChange={(event) => setClientDraft((current) => ({ ...current, notes: event.target.value }))} className="bg-neutral-950 border-neutral-800 rounded-sm min-h-[110px] text-white" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setClientDialogOpen(false)} className="border-neutral-700 rounded-sm hover:bg-neutral-800 hover:text-white">
                Cancelar
              </Button>
              <Button type="button" onClick={saveClientDraft} disabled={assigningClient} className="bg-[#F2B90D] hover:bg-[#DFA90C] text-black rounded-sm font-heading uppercase">
                <Save className="w-4 h-4" /> {assigningClient ? "Guardando" : "Guardar cliente"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
